import { useEffect, useState, useRef, lazy, Suspense } from "react";
import moment from "moment";
import {
    CircularProgress,
    Menu,
    MenuItem,
    Typography,
    Button,
    Divider,
    FormHelperText,
    IconButton,
} from "@mui/material";
import { tss } from "tss-react";
import { Scrollbars } from "react-custom-scrollbars";
import { useHistory } from "react-router-dom";
import AuthService from "services/AuthService";
import { getCachedData } from "api/Cache";
import ApiUtils from "api/Utils";
import { BreadCrumbs, ProgressBar, FileIcon } from "components";
import { EntityProperties } from "panels";
import * as Utils from "services/UtilsService";
import EventsEmitter from "events/EventsEmitter";
import Timeline, { TimelineMarkers, TodayMarker } from "react-calendar-timeline";
import { TimelineActions } from "actions";
import { ItemRenderer, GroupRenderer } from "renderers";
import ViewerEvents from "events/ViewerEvents";
import UploadVersionPanel from "panels/UploadVersionPanel";
import { config } from "config";
import { findElement } from "actions/ViewerTools";
import { Close, FolderOpen, Tv, TableChart } from "@mui/icons-material";
// make sure you include the timeline stylesheet or the timeline will not be styled
import "styles/Timeline.css";
import commonStyles, { panelBackgroundColor } from "styles/common";
import viewsStyles from "styles/views";
import leftStyles from "styles/leftPanel";

const useStyles = tss.create(() => ({
    header: {
        position: "relative",
        flexDirection: "row",
        paddingLeft: 12,
        paddingTop: 12,
        width: "100%",
        alignItems: "center",
        verticalAlign: "top",
    },
    tree: {
        paddingTop: 7.75,
        paddingBottom: 7.75,
        height: 55,
        maxWidth: "100%",
        backgroundColor: panelBackgroundColor,
        boxShadow: "1px 1px 13px 0px #00000012",
        width: "100%",
        flexGrow: 0,
    },
    noResult: {
        display: "inline-flex",
        alignItems: "center",
        height: 40,
    },
    button: {
        height: 12,
        width: 12,
    },
    closeButton: {
        position: "absolute",
        top: 4,
        right: 1,
    },
    statusRow: {
        display: "inline-flex",
        flexDirection: "row",
        paddingLeft: 10,
        height: 32,
        width: "100%",
        alignItems: "center",
        justifyContent: "space-around",
    },
    statusDiv: {
        width: 200,
        height: "100%",
        display: "inline-flex",
        alignItems: "center",
    },
    statusLabel: {
        textAlign: "left",
        color: "gray",
        margin: 0,
        padding: 0,
    },
    statusValue: {
        textAlign: "left",
        color: "gray",
        fontSize: 18,
        margin: 0,
        padding: 0,
    },
}));

function StatusBar(props: { item: any; entities: any }) {
    const { classes: common } = commonStyles();
    const { classes } = useStyles();
    const { item, entities } = props;
    var entity: any,
        path: string = "";
    if (item && entities) {
        var folder_id = item["entity_id"];
        entity = entities[folder_id];
        //console.log("entity:", entity);
        var curPath: any = TimelineActions.getPath(entity, entities);
        path = curPath.join(" > ");
    }

    return (
        <div
            style={{
                display: "inline-flex",
                flexDirection: "row",
                width: "100%",
                backgroundColor: "#f7f7f7",
                borderTop: "solid 1px #eee",
                height: 50,
                minHeight: 50,
                maxHeight: 50,
            }}
        >
            {item ? (
                <div
                    style={{
                        display: "inline-flex",
                        flexDirection: "row",
                        width: "100%",
                        backgroundColor: "#f7f7f7",
                        borderTop: "solid 1px #eee",
                    }}
                >
                    <div
                        className={classes.statusRow}
                        style={{ height: 50, width: "100%", justifyContent: "flex-start" }}
                    >
                        <div
                            style={{
                                display: "inline-flex",
                                height: "100%",
                                alignItems: "center",
                                justifyContent: "flex-start",
                            }}
                        >
                            <FileIcon icon={item["trackType"]["icon"]} />
                            <Typography
                                className={classes.statusValue}
                                noWrap
                                style={{ maxWidth: "calc(100% - 24px)" }}
                            >
                                {path}
                            </Typography>
                        </div>
                    </div>
                    <div
                        className={common.middle}
                        style={{
                            display: "inline-flex",
                            flexDirection: "column",
                            position: "relative",
                            bottom: 0,
                            width: "100%",
                            height: 50,
                        }}
                    >
                        <div className={classes.statusRow} style={{ height: 35 }}>
                            <div className={classes.statusDiv}>
                                <Typography className={classes.statusValue} noWrap>
                                    {Utils.formatDateWithTime(entity["capture_date"])}
                                </Typography>
                            </div>
                            <div className={classes.statusDiv}>
                                <Typography className={classes.statusValue} noWrap>
                                    {Utils.formatDateWithTime(entity["file_date"])}
                                </Typography>
                            </div>
                            <div className={classes.statusDiv}>
                                <Typography className={classes.statusValue} noWrap>
                                    {Utils.formatDateWithTime(entity["completion_time"])}
                                </Typography>
                            </div>
                            <div className={classes.statusDiv}>
                                <Typography className={classes.statusValue} noWrap>
                                    {typeof entity["version_active_date"] !== "undefined"
                                        ? Utils.formatDateWithTime(entity["version_active_date"])
                                        : "N/A"}
                                </Typography>
                            </div>
                        </div>
                        <div className={classes.statusRow} style={{ height: 25, marginTop: -11 }}>
                            <div className={classes.statusDiv}>
                                <FormHelperText className={classes.statusLabel}>Capture Date</FormHelperText>
                            </div>
                            <div className={classes.statusDiv}>
                                <FormHelperText className={classes.statusLabel}>Creation Date</FormHelperText>
                            </div>
                            <div className={classes.statusDiv}>
                                <FormHelperText className={classes.statusLabel}>Completion Time</FormHelperText>
                            </div>
                            <div className={classes.statusDiv}>
                                <FormHelperText className={classes.statusLabel}>Version Active Date</FormHelperText>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                ""
            )}
        </div>
    );
}

export function Title(props: { title: string; icon: string }) {
    const { classes, cx } = leftStyles();
    const { classes: common } = commonStyles();
    const { title, icon } = props;
    return (
        <div
            className={cx(classes.leftRoot, icon === "report" ? classes.relative : "")}
            style={{ top: 0, width: "max-content" }}
        >
            <div className={classes.header}>
                <div className={common.middle}>
                    <IconButton style={{ width: 40, height: 40 }} className={common.disableEventsNoShades}>
                        <FileIcon icon={icon} />
                    </IconButton>
                    <Typography className={common.mediumLabel} style={{ maxWidth: 200 }} noWrap>
                        {title}
                    </Typography>
                </div>
            </div>
        </div>
    );
}

function RenderedReport(props: {
    show: boolean;
    setShowReport: any;
    reportName: string;
    selectedReportId: string;
    data: any;
}) {
    const { show, setShowReport, selectedReportId, data } = props;
    const { classes } = useStyles();
    const handleCloseReport = () => {
        setShowReport(false);
    };
    const projectId = selectedReportId.split("|")[0];
    const reportId = selectedReportId.split("|")[1];
    //console.log("reportId:", reportId);
    const entity = data["entities"][projectId][reportId];
    //console.log("entity:", entity);
    return (
        <div
            style={{
                position: "relative",
                display: show ? "inline-flex" : "none",
                backgroundColor: "#fcfcfc",
                maxWidth: "50vw",
                width: "100%",
                maxHeight: "100%",
                flexDirection: "row",
                overflow: "hidden",
            }}
        >
            <div className={classes.closeButton}>
                <Button
                    className={classes.button}
                    onClick={(e) => {
                        handleCloseReport();
                    }}
                >
                    <Close className={classes.button} />
                </Button>
            </div>
            <div
                style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: "100%",
                    overflow: "hidden",
                }}
            >
                <EntityProperties entity={entity} maxHeight="calc(100% - 50px)" maxWidth="calc(100% - 10px)" />
            </div>
        </div>
    );
}

var EmbeddedViewer: any;

const requestAuthToken = () => {
    return AuthService.getToken();
};

export default function Workflows() {
    const { classes: viewsClasses } = viewsStyles();
    const { classes, cx } = useStyles();
    const { classes: common } = commonStyles();
    const data: any = getCachedData();
    const entities = data["entities"];
    const allViews = data["views"];
    const history = useHistory();
    const viewer = useRef();

    const [reload, setReload] = useState(false);
    const [filter, setFilter] = useState(Utils.StorageUtils.loadFromSessionStorage("filter") || "all");
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedViewId, setSelectedViewId] = useState("");
    const [selectedReportId, setSelectedReportId] = useState("");
    const [selectedProjectId, setSelectedProjectId] = useState("");
    const [viewId, setViewId] = useState("");
    const [reportName, setReportName] = useState("");
    const [showReport, setShowReport] = useState(false);
    const [workflowItems, setWorkflowItems] = useState<any>([]);
    const [workflowProject, setWorkflowProject] = useState<any>([]);
    const [workflowGroups, setWorkflowGroups] = useState<any>([]);
    const [openGroups, setOpenGroups] = useState<any>({});
    const [productsMap, setProductsMap] = useState<any>([]);
    const [versionInfo, setVersionInfo] = useState<any>(null);
    const [item, setItem] = useState<any>(null);
    const [spacing, setSpacing] = useState(0);
    const [show, setShow] = useState(false);

    useEffect(() => {
        var updateFilter = (filter: string) => {
            setFilter(filter);
            setReload(true);
            setItem(null);
        };
        EventsEmitter.on("updateFilter", updateFilter);
        return () => {
            EventsEmitter.removeEventListener("updateFilter", setFilter);
        };
    }, [reload]);

    useEffect(() => {
        var reload = (object: any) => {
            if (object["view_type"] === "container" || object["file_type"] === "REPORT") {
                setReload(true);
            }
        };
        EventsEmitter.on("signalr_update", reload);
        return () => {
            EventsEmitter.removeEventListener("signalr_update", reload);
        };
    }, []);

    useEffect(() => {
        if (viewId !== "") {
            EmbeddedViewer = lazy(() => import("@voxelfarm/voxelfarm-react-viewer"));
            setTimeout(() => setShow(true), 200);
        }
        return () => {
            setShow(false);
        };
    }, [viewId]);

    useEffect(() => {
        if (data.loaded || reload) {
            var productMap: any = {},
                workflows: any = {},
                curWorkflowProject: any = [],
                curWorkflowItems: any = [],
                curWorkflowGroups: any = [],
                curGroups: any = [];
            var list = Object.entries(entities);
            if (filter !== "all") {
                list = list.filter((entries: any) => {
                    return entries[0] === filter;
                });
            }
            list.forEach((entry: any) => {
                var projectId = entry[0];
                var objects = entry[1];
                var index = 0;
                curWorkflowProject[projectId] = objects;
                index = TimelineActions.CreateProductMap(
                    projectId,
                    objects,
                    workflows,
                    curWorkflowGroups,
                    curWorkflowItems,
                    productMap,
                    index,
                    "capture_date"
                );
                if (!curGroups[projectId]) {
                    curGroups[projectId] = [];
                }
                if (!curGroups[projectId][index + 1]) {
                    curGroups[projectId] = TimelineActions.resize(curGroups[projectId], index + 1, false);
                }
            });
            const itemsChanged = curWorkflowGroups.length !== workflowGroups.length;
            curWorkflowItems = curWorkflowItems.filter((item: any) => item.trackType);
            if (itemsChanged) {
                //console.log("curWorkflowGroups:", curWorkflowGroups);
                //console.log("curGroups:", curGroups);
                setWorkflowItems(curWorkflowItems);
                setWorkflowGroups(curWorkflowGroups);
                setOpenGroups(curGroups);
                setWorkflowProject(curWorkflowProject);
                setProductsMap(productMap);
            }
            setReload(false);
        }
    }, [data, filter, reload, entities, workflowGroups.length]);

    if (!data.loaded) {
        return <CircularProgress />;
    }

    var views: any = [],
        reports: any = [],
        itemName: string = "";

    const isParentOpen = (g: any) => {
        if (g.level === 0) return true;
        const split: any = g.parent.split("-");
        const projectId: string = split[0];
        const parentIndex: number = split[1];
        //console.log("g:", g);
        //console.log("openGroups:", parentIndex, "|", openGroups[projectId][parentIndex]);
        if (openGroups[projectId][parentIndex] && isParentOpen(workflowGroups.find((k: any) => k["id"] === g.parent)))
            return true;
        return false;
    };

    const getOpenedGroups = (workflowGroups: any) => {
        const filtered: any = workflowGroups.filter((g: any) => isParentOpen(g));
        //console.log("filtered:", filtered);
        const rendered: any = filtered.map((group: any) => {
            return Object.assign({}, group, {
                title: group.root ? (
                    <div key={"filtered-" + group.id} style={{ cursor: "pointer" }}>
                        {group.title}
                    </div>
                ) : (
                    <div key={"filtered-" + group.id}>{group.title}</div>
                ),
            });
        });
        return rendered;
    };

    const openedGroups = getOpenedGroups(workflowGroups);
    /*
    console.log("workflowGroups:", workflowGroups);
    console.log("openedGroups:", openedGroups);
    console.log("openGroups:", openGroups);
    console.log("productMap:", productsMap);
    console.log("workflowItems:", workflowItems);
    */
    //console.log("item:", item);
    if (item && item !== null) {
        var projectId = item["id"].split("-")[0];
        let folderId = item["entity_id"];
        var projectEntities: any = workflowProject[projectId];
        for (let i in projectEntities) {
            let entity = projectEntities[i];
            if (
                entity["file_folder"] === folderId &&
                entity["file_type"] === "VIEW" &&
                entity["view_type"] === "container"
            ) {
                views.push([entity["ID"], entity["name"]]);
            }
            if (entity["file_folder"] === folderId && entity["file_type"] === "REPORT") {
                reports.push([projectId + "|" + entity["ID"], entity["name"]]);
            }
        }
        if (projectEntities && projectEntities[folderId]) {
            var curEntity = projectEntities[folderId];
            var curPath: any = TimelineActions.getPath(curEntity, projectEntities);
            itemName = curPath.join(" > ");
        }
    }

    const handleClick = (event: any) => {
        setAnchorEl(event.target);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleItemSelection = (item: any) => {
        setItem(item);
    };

    const groupRenderer = ({ group }: any) => {
        return (
            <GroupRenderer group={group} openGroups={openGroups} toggleGroup={toggleGroup} addVersion={addVersion} />
        );
    };

    const itemRenderer = (props: any) => {
        return <ItemRenderer handleItemSelection={handleItemSelection} {...props} />;
    };

    const toggleGroup = (group: any) => {
        var id = group["id"].split("-")[1];
        var projectId = group["project"];
        //console.log(id);
        const curGroup: any = { ...openGroups };
        curGroup[projectId][id] = !curGroup[projectId][id];
        setOpenGroups(curGroup);
        setSelectedProjectId(projectId);
    };

    const showView = (id: string) => {
        setSelectedViewId(id);
        var curView = views.find((view: any) => view[0] === id);
        if (curView) {
            if (Boolean(config.api.filters === "true") && curView) {
                const view: any = projectEntities[curView[0]];
                if (view) {
                    let updateList: any = [view.project];
                    let entityContainer = view["entity_container"].split(" ");
                    entityContainer = Utils.cleanArray(entityContainer);
                    entityContainer.forEach((layerId: string) => {
                        const layer = findElement(view.project, layerId);
                        if (layer && layer["view_type"]) {
                            updateList.push(layer["view_type"]);
                        }
                    });
                    updateList = updateList.concat(entityContainer);
                    ApiUtils.getRemoteObjects(updateList, view.project, () => {
                        setViewId(curView[0]);
                    });
                }
            } else {
                setViewId(curView[0]);
            }
        }
        handleClose();
    };

    const showCurrentReport = (id: string) => {
        setSelectedReportId(id);
        var curReport = reports.find((report: any) => report[0] === id);
        if (curReport) {
            setReportName(curReport[1]);
        }
        setShowReport(true);
        handleClose();
    };

    const viewCatalog = (item: any) => {
        const folderId = item["entity_id"];
        history.push("/catalog/" + folderId);
    };

    const handleItemDeselection = (itemId: any, e: any, time: any) => {
        //setSelectedProjectId("");
        setItem(null);
    };

    const handleItemContextMenu = (itemId: any, e: any, time: any) => {
        setSelectedProjectId(itemId.split("-")[0]);
        handleClick(e);
    };

    const addVersion = (group: any) => {
        var info = group["id"].split("-");
        var project = info[0];
        var productId = parseInt(info[1]);
        var projectProducts = productsMap[project];
        var productEntry: any = Object.entries(projectProducts).find((productEntry) => productEntry[1] === productId);
        var productName: string = productEntry[0];
        if (productName.includes(":version")) {
            productName = productName.replace(":version", "");
        }
        setVersionInfo({ product: productName, project: project });
    };

    const handleVersionClose = () => {
        setVersionInfo(null);
    };

    const handleUpdate = (evt: any) => {
        if (evt.scrollHeight - evt.clientHeight > 0) {
            setSpacing(7);
        } else {
            setSpacing(0);
        }
    };
    return (
        <div className={`voxelViewer  ${viewsClasses.main}`} style={{ width: "100%", height: "100%" }}>
            <div
                className={cx(common.middle, viewsClasses.topHeader)}
                style={{ display: Object.entries(workflowGroups).length === 0 ? "flex" : "none" }}
            >
                <BreadCrumbs label="Workflows" />
            </div>
            {Object.entries(workflowGroups).length === 0 ? (
                <div className={cx(common.middle, classes.tree)}>
                    <Typography className={classes.noResult} style={{ paddingLeft: 24 }}>
                        No Workflow
                    </Typography>
                </div>
            ) : (
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            minHeight: "50%",
                            height: "100%",
                            position: "relative",
                            display: "inline-flex",
                            flexDirection: "row",
                        }}
                    >
                        <div
                            style={{
                                display: "inline-flex",
                                backgroundColor: "#7c7c7c",
                                minWidth: "50%",
                                maxWidth: "100%",
                                width: "100%",
                                height: "100%",
                                maxHeight: "100%",
                            }}
                        >
                            {viewId !== "" && selectedProjectId !== "" && selectedViewId !== "" ? (
                                <Title title={allViews[viewId]["name"]} icon="view" />
                            ) : (
                                ""
                            )}
                            {selectedProjectId !== "" && selectedViewId !== "" ? <ProgressBar height={4} /> : ""}
                            {selectedProjectId !== "" &&
                            selectedViewId !== "" &&
                            EmbeddedViewer &&
                            EmbeddedViewer !== null &&
                            show ? (
                                <Suspense
                                    fallback={
                                        <div className={common.middleCenter} style={{ width: "100%", height: "100%" }}>
                                            Loading...
                                        </div>
                                    }
                                >
                                    <EmbeddedViewer
                                        ref={viewer}
                                        requestAuthToken={requestAuthToken}
                                        projectId={selectedProjectId}
                                        streamingUrl={config.viewer.streamingUrl}
                                        restUrl={config.api.baseUrl}
                                        restEndpoints={config.api.endpoints}
                                        style={{ width: "100%", height: "100%" }}
                                        showControlsInView={true}
                                        viewId={selectedViewId}
                                        onBusy={ViewerEvents.onBusy}
                                        enableLoadAttributes={true}
                                        project={projectEntities}
                                    />
                                </Suspense>
                            ) : (
                                ""
                            )}
                        </div>
                        {selectedReportId !== "" ? (
                            <RenderedReport
                                data={data}
                                reportName={reportName}
                                selectedReportId={selectedReportId}
                                show={showReport}
                                setShowReport={setShowReport}
                            />
                        ) : (
                            ""
                        )}
                    </div>
                    <div
                        style={{
                            position: "relative",
                            bottom: 0,
                            width: "100%",
                            maxHeight: "calc(50% - 50px)",
                            flexGrow: 1,
                            height: "auto",
                            backgroundColor: "#f7f7f7",
                        }}
                    >
                        <Scrollbars
                            autoHeight
                            autoHeightMax={"100%"}
                            style={{ height: "100%", flexGrow: 1 }}
                            onUpdate={handleUpdate}
                        >
                            <Timeline
                                style={{ width: "calc(100% - " + spacing + "px)" }}
                                groups={openedGroups}
                                items={workflowItems}
                                itemHeightRatio={0.8}
                                sidebarWidth={410}
                                useResizeHandle={false}
                                groupRenderer={groupRenderer}
                                itemRenderer={itemRenderer}
                                defaultTimeStart={moment().add(-30, "day")}
                                defaultTimeEnd={moment().add(12, "hour")}
                                stackItems
                                onItemContextMenu={handleItemContextMenu}
                                onItemDeselect={handleItemDeselection}
                            >
                                <TimelineMarkers>
                                    <TodayMarker />
                                </TimelineMarkers>
                            </Timeline>
                        </Scrollbars>
                        <Menu
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right",
                            }}
                            MenuListProps={{
                                style: {
                                    width: "fit-content",
                                    overflow: "hidden",
                                },
                            }}
                        >
                            {item ? (
                                <div style={{ display: "inline-flex", padding: 10, paddingTop: 3 }}>
                                    <FileIcon icon={item["trackType"]["icon"]} />
                                    <Typography noWrap>{itemName}</Typography>
                                </div>
                            ) : (
                                ""
                            )}
                            {views.length > 0 ? <Divider /> : ""}
                            {views.map((item: any, index: number) => (
                                <MenuItem key={"view-1-" + index} onClick={(e: any) => showView(item[0])}>
                                    <Tv style={{ paddingRight: 5 }} />
                                    <Typography noWrap>{item[1]}</Typography>
                                </MenuItem>
                            ))}
                            {reports.length > 0 ? <Divider /> : ""}
                            {reports.map((item: any, index: number) => (
                                <MenuItem key={"report-1-" + index} onClick={(e: any) => showCurrentReport(item[0])}>
                                    <TableChart style={{ paddingRight: 5 }} />
                                    <Typography noWrap>{item[1]}</Typography>
                                </MenuItem>
                            ))}
                            <Divider />
                            <MenuItem onClick={() => viewCatalog(item)}>
                                <FolderOpen style={{ width: 20, height: 20, marginRight: 5 }} />
                                <Typography noWrap>Show in Catalog</Typography>
                            </MenuItem>
                        </Menu>
                    </div>
                    <StatusBar item={item} entities={workflowProject[projectId]} />
                    {versionInfo !== null ? (
                        <UploadVersionPanel
                            open={true}
                            handleClose={handleVersionClose}
                            product={versionInfo["product"]}
                            project={selectedProjectId}
                        />
                    ) : (
                        ""
                    )}
                </div>
            )}
        </div>
    );
}
